import React, { useMemo } from "react";
import { DataGridPro } from "@mui/x-data-grid-pro";

const TotalValueQC = ({
  rows = [],
  visibleColumns = [],
  getQuotaDetails,
  currentWeek = 0,
}) => {
  // Calculate totals based on rows data
  const totalRow = useMemo(() => {
    if (!rows || rows.length === 0) {
      return {};
    }

    const totals = {
      id: "total",
      unusedQuota: 0,
      quota: 0,
      consumption: 0,
      availableQuota: 0,
      intendedQuantity: 0,
      todaysConsumption: 0,
      yesterdaysConsumption: 0,
      deliveredQuantity: 0,
      sit: 0,
      weekly1: 0,
      weekly2: 0,
      weekly3: 0,
      weekly4: 0,
      weekly5: 0,
    };

    rows.forEach((row) => {
      // Sum basic fields
      totals.unusedQuota += row.unusedQuota || 0;
      totals.quota += row.quota || 0;
      totals.consumption += row.consumption || 0;
      totals.intendedQuantity += row.intendedQuantity || 0;
      totals.todaysConsumption += row.todaysConsumption || 0;
      totals.yesterdaysConsumption += row.yesterdaysConsumption || 0;
      totals.deliveredQuantity += row.deliveredQuantity || 0;
      totals.sit += row.sit || 0;

      // Calculate AvailableQuota based on depletion model
      let availableQuota = 0;
      if (getQuotaDetails?.depletionModel?.toLowerCase() === "none") {
        availableQuota = (row.weekly1 || 0) - (row.consumption || 0);
      } else if (getQuotaDetails?.depletionModel?.toLowerCase() === "biweekly") {
        if (currentWeek === 1) {
          availableQuota = (row.weekly1 || 0) - (row.consumption || 0);
        } else {
          availableQuota = (row.weekly2 || 0) - (row.consumption || 0);
        }
      } else {
        if (currentWeek === 1) {
          availableQuota = (row.weekly1 || 0) - (row.consumption || 0);
        } else if (currentWeek === 2) {
          availableQuota = (row.weekly2 || 0) - (row.consumption || 0);
        } else if (currentWeek === 3) {
          availableQuota = (row.weekly3 || 0) - (row.consumption || 0);
        } else if (currentWeek === 4) {
          availableQuota = (row.weekly4 || 0) - (row.consumption || 0);
        } else {
          availableQuota = (row.weekly5 || 0) - (row.consumption || 0);
        }
      }
      totals.availableQuota += availableQuota;

      // Sum weekly values based on depletion model
      const depletionModel = getQuotaDetails?.depletionModel?.toLowerCase();
      if (depletionModel === "none") {
        totals.weekly1 += row.weekly1 || 0;
      } else if (depletionModel === "biweekly") {
        totals.weekly1 += row.weekly1 || 0;
        totals.weekly2 += row.weekly2 || 0;
      } else {
        totals.weekly1 += row.weekly1 || 0;
        totals.weekly2 += row.weekly2 || 0;
        totals.weekly3 += row.weekly3 || 0;
        totals.weekly4 += row.weekly4 || 0;
        totals.weekly5 += row.weekly5 || 0;
      }
    });

    return totals;
  }, [rows, getQuotaDetails, currentWeek]);

  // Filter columns to only show the required ones
  const totalColumns = useMemo(() => {
    const allowedFields = [
      "unusedQuota",
      "quota",
      "consumption",
      "availableQuota",
      "intendedQuantity",
      "todaysConsumption",
      "yesterdaysConsumption",
      "deliveredQuantity",
      "sit",
      "weekly1",
      "weekly2",
      "weekly3",
      "weekly4",
      "weekly5",
    ];

    const depletionModel = getQuotaDetails?.depletionModel?.toLowerCase();

    return visibleColumns
      .filter((col) => allowedFields.includes(col.field))
      .map((col) => {
        // Create a simplified column for totals (remove renderCell, renderHeader, etc.)
        let baseColumn = {
          ...col,
          renderCell: undefined,
          valueGetter: undefined,
          sortable: false,
          cellClassName: undefined,
          headerClassName: undefined,
        };

        // Handle unusedQuota column with custom renderHeader
        if (col.field === "unusedQuota") {
          baseColumn = {
            ...baseColumn,
            headerName: "", // Clear headerName to use renderHeader
            minWidth: 130,
            width: 150,
            renderHeader: () => (
              <div style={{ 
                lineHeight: "1.15", 
                textAlign: "center",
                whiteSpace: "normal",
                fontWeight: "bold",
                fontSize: "13px",
                fontFamily: "Montserrat, Sans-serif",
                padding: "4px 2px",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
                width: "100%",
                boxSizing: "border-box"
              }}>
                <span style={{ display: "block", lineHeight: "1.2" }}>Unused Quota -</span>
                <span style={{ display: "block", lineHeight: "1.2" }}>Previous Period</span>
              </div>
            ),
          };
        } else {
          // For other columns, remove renderHeader
          baseColumn.renderHeader = undefined;
          
          // Update headerName based on depletion model for weekly columns
          if (col.field === "weekly1") {
            if (depletionModel === "none") {
              baseColumn.headerName = "Final Quota";
            } else if (depletionModel === "biweekly") {
              baseColumn.headerName = "First Half";
            } else {
              baseColumn.headerName = "Week1";
            }
          } else if (col.field === "weekly2") {
            if (depletionModel === "biweekly") {
              baseColumn.headerName = "Second Half";
            } else {
              baseColumn.headerName = "Week2";
            }
          }
        }

        // Add valueFormatter for number formatting if not already present
        if (!baseColumn.valueFormatter) {
          baseColumn.valueFormatter = (params) => {
            if (params.value == null || params.value === undefined) return "0";
            const numValue = Number(params.value);
            if (isNaN(numValue)) return "0";
            return numValue.toLocaleString("en-US");
          };
        }

        return baseColumn;
      });
  }, [visibleColumns, getQuotaDetails]);

  if (!rows || rows.length === 0 || totalColumns.length === 0) {
    return null;
  }

  return (
    <DataGridPro
        sx={{
          fontFamily: "Montserrat, Sans-serif",
          "& .MuiDataGrid-columnHeaderTitleContainer": {
            fontWeight: "bold !important",
            fontSize: "14px",
            fontFamily: "Montserrat, Sans-serif",
          },
          "& .MuiDataGrid-columnHeaderTitle": {
            whiteSpace: "normal",
            lineHeight: "normal",
            fontWeight: "bold",
            fontSize: "14px",
            fontFamily: "Montserrat, Sans-serif",
          },
          "& .MuiDataGrid-columnHeader[data-field='unusedQuota']": {
            "& .MuiDataGrid-columnHeaderTitleContainer": {
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              whiteSpace: "normal",
              lineHeight: "1.1",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              textAlign: "center",
            },
          },
          "& .MuiDataGrid-row": {
            fontWeight: "bold",
          },
        }}
        rows={[totalRow]}
        columns={totalColumns}
        getRowId={(row) => row.id}
        hideFooter
        disableColumnMenu
        disableColumnSorting
        disableRowSelectionOnClick
        autoHeight
        rowHeight={40}
        headerHeight={70}
      />
  );
};

export default TotalValueQC;

